import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { FLOAT_TEXTURES_OK } from '../float-textures.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// The structure IS the light - sixteen thousand lamps held in the air. A rig
// would be a second, contradictory source.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'airframe',
  family: 'particles',
  danceability: 0.75,
  requiresTier: 1,
  requiresFloatTextures: true,
  energyLevel: 0.84,
  bpmMin: 105,
  bpmMax: 175,
  movement: 0.80,
  tonality: 0.75,
  prefers: { gridStrength: [0.50, 1.0] },
  enabled: FLOAT_TEXTURES_OK
};

// Airframe: a volumetric structure held in the air by a swarm of lights, which
// the camera orbits, approaches and FLIES THROUGH.
//
// WHAT SEPARATES IT FROM SWARM. Swarm does FORMATION: points reorganise into
// planar shapes and solids, seen face-on, and the gesture is the transition.
// Airframe does STRUCTURE: one three-dimensional object, nearly still, and the
// gesture is the CAMERA MOVE. The structure changes rarely (once every four
// phrases); the camera never stops.
//
// The move no real drone show can do is going INSIDE. A drone show is watched
// from a field, from outside, always. Everything here is arranged around being
// able to pass through the middle of it: the structures are hollow wireframes
// with a traversable interior, the points are additive with no depth write so
// the far side stays visible through the near side, and the point size range
// opens up during the traversal so that near and far points differ by nearly
// 9:1 - which is what gives the thing its scale.
//
// THE POINTS ARE ON THE EDGES, not in the volume. This is the single decision
// that makes the form readable while orbiting: points filling a volume become
// an opaque cloud from every angle after the first, and no amount of
// attenuation recovers the silhouette. Each structure is generated as a LIST OF
// EDGES at startup and the points are sampled along them.
//
// Positions live in float ping-pong textures (Swarm's infrastructure, reused as
// is: same FeedbackBuffer, same two-pass velocity/position simulation, same
// sorted assignment, same explosion) - the documented exception to the
// no-render-target contract.

// ---------------------------------------------------------------- layout ----
// THE STRUCTURE'S RADIUS. Everything else is expressed as a multiple of it, so
// the framing survives a change of scale.
//
// 3.4 is not arbitrary: at the fov this scene runs at (68 degrees) a camera at
// 1.6 x S sees 1.079 x S of half-height, so a structure of half-extent S fills
// the frame vertically with a hair of margin. And it is the scale at which the
// edge densities come out right - see EDGE_DENSITY below.
const S = 3.4;

// Points per unit of edge length, the brief's 40-60 band. This is a CONSEQUENCE
// of S and of how finely each structure is trussed, not a free parameter: with
// 16 129 points, a density of 50 means 320 units of total edge length, and that
// is about what a frequency-3 geodesic sphere of radius 3.4 has. Every other
// structure's subdivision counts were then chosen against the same measurement.
// ?airdensity=1 prints what each one actually achieves; measured, tier 2 is
//   sphere 44.4  tower 56.4  ring 46.6  arch 50.8  lattice 47.8  spiral 151.5
// and tier 1 (coarsened structures, see below)
//   sphere 38.0  ring 46.9  arch 40.0  lattice 41.8
// Two misses, both understood. The tier-1 sphere is 5% low because geodesic
// frequency is an integer: frequency 2 gives 38 and frequency 3 gives 25, and
// 38 is the closer of the two. The spiral is three times over, and that one is
// a deliberate trade - see makeSpiral, where every way of supplying the missing
// length destroys the structure's legibility. Both are compensated in
// brightness rather than pretended away (see REF_DENSITY).
const EDGE_DENSITY_MIN = 40;
const EDGE_DENSITY_MAX = 60;
// The density the render level is calibrated against. Brightness per unit of
// edge is (points per unit) x (intensity), so holding the six structures at the
// same apparent brightness would mean intensity ~ 1/density - the same
// reasoning Swarm applies to its shapes' coverage. Without any correction the
// spiral, which runs three times denser than the sphere, was three times
// brighter. With the full 1/density it went the other way: the spiral also has
// the least total edge, so correcting per unit made the whole object 2.3x
// dimmer than the sphere. The square root splits the two, the same compromise
// the tier gain makes.
const REF_DENSITY = 48;
const INTENSITY_CLAMP = [0.30, 1.6];

const TIER = {
  // No tier 0 by contract: at tier 0 the point count has to fall far enough
  // that the edges stop reading as edges, and a structure whose edges have
  // dissolved is just a cloud - which is Swarm's job, and Swarm has a real
  // tier-0 layout. requiresTier 1 gates selection; the constructor also clamps,
  // so a live downgrade by the frame-time governor lands on the tier-1 layout
  // rather than on undefined.
  1: { n: 95, structures: ['sphere', 'arch', 'ring', 'lattice'], stars: 240, fog: false },
  2: { n: 127, structures: ['sphere', 'tower', 'ring', 'arch', 'lattice', 'spiral'], stars: 420, fog: true }
};
const DEFAULT_TIER = 2;
const REF_COUNT = 127 * 127;   // tier 2's count, the brightness reference

// Colour zones. The brief's 4-6; 5 divides every one of the six structures into
// bands that read as bands (6 is too many on the 5-cell lattice, 4 too few on
// the 6-storey tower).
const ZONES = 5;
const ZONE_TEX_W = 8;          // padded to a round width; only ZONES are used

// ------------------------------------------------------------- simulation ---
const FIXED_DT = 1 / 60;
const MAX_SUBSTEPS = 2;
// THE STATION-KEEPING SPRING, and the one number here that had to be measured
// rather than reasoned about.
//
// The obvious choice - a moderate spring at a damping ratio of 0.8 - does not
// work, and the way it fails is invisible unless you measure it. With the
// wobble switched off (?airstable=1) the swarm never came to rest: it sat at an
// rms of 0.029 units from its targets forever, about 1% of the structure's
// radius and three screen pixels. That ruins the thing the instability is for.
// The designed wobble measures 0.039 rms, so ON and OFF differed by a factor of
// 1.4 - the comparison ?airstable=1 exists to make could not be made, and what
// the brief calls the difference between a swarm and a geometry was mostly
// noise either way.
//
// The cure is in the integrator, not in the physics. Velocity is stepped as
//   vel = vel * (1 - DAMPING * dt) + (spring) * dt
// so DAMPING * dt = 1 annihilates the velocity's memory every step: whatever
// excites the swarm cannot be carried forward and accumulated. Measured across
// a sweep of (stiffness, damping), that one condition is what matters - at
// damping 45 the residual is 0.0025, at 60 it is exactly zero:
//   k=30  c=8.8  still 0.0286   k=60  c=30  still 0.0078
//   k=120 c=45   still 0.0025   k=150 c=60  still 0.0000, wobble 0.040
// DAMPING is therefore written as 1/FIXED_DT rather than as a number, because
// it is not independent of it. The stiffness then has to rise with it to keep
// the settling time (error decays by 1 - k*dt^2 per step, so 150 gives a time
// constant of 0.39 s and a structure change that completes in 3 s, inside the
// brief's 3-5 s window).
const ATTRACT = 150.0;
const ATTRACT_TIGHTEN = 1.7;   // before a drop
// Dead-beat: see ATTRACT above. Not a free parameter - it is tied to FIXED_DT.
const DAMPING = 1 / FIXED_DT;
// A point crosses at most 2 x S = 6.8 units between two structures; at this cap
// that is 2.4 s, plus the 1.4 s stagger span = a 3.4-3.8 s metamorphosis, which
// is the brief's 3-5 s window. The cap is also what makes the change happen IN
// FORMATION: without it the spring alone moves a distant point most of the way
// in a single frame.
const MAX_SPEED = 2.8;
const STAGGER_SPAN_S = 1.4;
const MORPH_S = 4.2;           // how long the camera treats a change as ongoing

// THE INSTABILITY. 0.8% of the structure's size (2 x S), which at the orbit
// distance is about 8 screen pixels at 1080p - small enough to be a defect,
// large enough to see. A perfectly rigid structure reads as a 3D mesh; this is
// the whole difference, and ?airstable=1 is the demonstration.
const WOBBLE_AMP = 0.008 * (2 * S);
const WOBBLE_LO = 0.6;         // bands.low -> amplitude
const WOBBLE_HI = 1.8;
const WOBBLE_RATE = 0.35;      // Hz, the base rate of the slow noise
// Miscalibrated machines: this fraction of the swarm holds position four times
// worse than the rest. Every real drone show has them.
const MISCAL_FRACTION = 0.01;
const MISCAL_GAIN = 4.0;

// Against dead-beat damping the impulse is a terminal VELOCITY in disguise
// (v = impulse / DAMPING), so it has to be read that way: 150 / 60 = 2.5 units
// per second, about a third of the radius over the explosion's one beat, which
// reads as an explosion. 24 - the value carried over from Swarm, which damps
// far more gently - moved the structure by 8% of its radius, a breath.
const EXPLODE_IMPULSE = 150.0;
const EXPLODE_BEATS = 1;
const RECOMPOSE_BEATS = 2;
const TIGHTEN_BEATS = 2;

// ----------------------------------------------------------------- camera ---
const ORBIT_RADIUS = 1.60 * S;
const APPROACH_RADIUS = 1.02 * S;   // grazing the surface
// 1.10, not 1.30: the traversal exits looking AWAY from the structure, so
// every unit it travels past the far surface is a frame with nothing in it.
// Measured at 1.30, the last 22% of the traversal - most of a beat - was empty
// sky. At 1.10 it comes out just clear of the surface, and the turn below
// covers the rest.
const THROUGH_HALF = 1.10 * S;      // where the traversal comes out
const RETREAT_RADIUS = 2.40 * S;    // the whole structure in frame
const MORPH_RADIUS = 2.05 * S;      // while the structure is metamorphosing

const ORBIT_BEATS = 16;             // one revolution
const APPROACH_BEATS = 4;
const THROUGH_BEATS = 4;
const RETREAT_BEATS = 4;
const ORBIT_SETTLE_BEATS = 4;       // radius eases back over the first four

const CAM_SPEED_LO = 0.75;          // bands.mid -> orbit rate
const CAM_SPEED_HI = 1.40;
const PHI_MIN = -0.26, PHI_MAX = 0.44;   // elevation band, radians
const PHI_TAU = 2.6;                     // elevation easing time constant

const STRUCTURE_PHRASES = 4;        // a structure holds four phrases
const NO_GRID_STRUCT_S = 24;        // fallback cadence with no beat grid
const NO_GRID_BPM = 120;

// ----------------------------------------------------------------- render ---
// 18 is set from the orbit: at the near edge of the structure (depth 2.04) it
// gives 8.8 px, at the far edge (8.84) it bottoms out on the 3 px floor.
const SIZE_SCALE = 18.0;
const POINT_SIZE_MIN = 3.0;
const POINT_SIZE_MAX = 12.0;
// THE TRAVERSAL'S SIZE RATIO, which is the brief's criterion 2. The ceiling
// has to be set from the FAR side, not the near one: the near points clamp to
// it whatever it is, but the far side of a structure is only about 5.2 units
// away at the tightest (the tower and the spiral, which are narrow), so it
// bottoms out at 3.45 px rather than at the 3 px floor. 30 / 3.45 = 8.7,
// and the wider structures reach 9.8. At 26 the narrow ones measured 6.9.
// Raising the ceiling only for the traversal is what keeps the orbit from
// looking like a field of blobs.
const THROUGH_SIZE_MAX = 30.0;
// 0.70, not 0.85. Additive points pile up on the SILHOUETTE - the edges seen
// end-on, where the structure is deepest - and at 0.85 that rim clipped to
// white on 2.5% of the frame, which turned the outline of every structure the
// same yellow-white whatever colour its zones were.
const BASE_INTENSITY = 0.70;
const FOG_REF = 3.0;           // no attenuation nearer than this
const FOG_K = 0.16;
const FOG_FLOOR = 0.30;        // the far side is dimmed, never extinguished
const SAFE_FLOOR = 0.5;        // the brief's floor for this chunk
const STAR_RADIUS_MIN = 34, STAR_RADIUS_MAX = 46;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function smoother(t) { const c = clamp01(t); return c * c * c * (c * (c * 6 - 15) + 10); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function idle(fn) {
  if (typeof requestIdleCallback === 'function') requestIdleCallback(fn, { timeout: 400 });
  else setTimeout(fn, 0);
}

// ============================================================================
// STRUCTURES - generated once at startup as lists of EDGES.
//
// Each builder returns { edges: Float32Array (6 floats per edge: ax,ay,az,
// bx,by,bz), zone: (x,y,z) -> 0..ZONES-1 }. The zone function is the
// structure's own natural coordinate - height, angle, or distance to the
// centre - quantised to an integer, never blended: the brief's hard borders
// are what make the colouring read as a drone show's rather than as a gradient
// map.
// ============================================================================

function EdgeList() {
  this.a = [];
}
EdgeList.prototype.push = function (ax, ay, az, bx, by, bz) {
  this.a.push(ax, ay, az, bx, by, bz);
};
EdgeList.prototype.pushV = function (p, q) {
  this.a.push(p[0], p[1], p[2], q[0], q[1], q[2]);
};
EdgeList.prototype.done = function () { return new Float32Array(this.a); };

// Quantised band helper: value in [-1, 1] -> 0..ZONES-1, borders exact.
function band(t) {
  return Math.max(0, Math.min(ZONES - 1, Math.floor((t * 0.5 + 0.5) * ZONES)));
}

// --- a square-section truss following a path --------------------------------
// The shared builder behind TOWER and ARCH. A station is { p, u, v, half }:
// a centre, two unit vectors spanning its cross-section, and a half-width. It
// produces the four longitudinal chords, a ring at every station, and X-bracing
// on all four faces of every bay - which is what a real lattice tower or truss
// arch is made of, and what gets the edge density into the band.
function trussEdges(out, stations, floors) {
  const corner = function (s, k) {
    const su = (k === 0 || k === 3) ? 1 : -1;
    const sv = (k === 0 || k === 1) ? 1 : -1;
    return [
      s.p[0] + (s.u[0] * su + s.v[0] * sv) * s.half,
      s.p[1] + (s.u[1] * su + s.v[1] * sv) * s.half,
      s.p[2] + (s.u[2] * su + s.v[2] * sv) * s.half
    ];
  };
  for (let i = 0; i < stations.length; i++) {
    const c = [corner(stations[i], 0), corner(stations[i], 1), corner(stations[i], 2), corner(stations[i], 3)];
    // the ring
    for (let k = 0; k < 4; k++) out.pushV(c[k], c[(k + 1) % 4]);
    if (i + 1 < stations.length) {
      const d = [corner(stations[i + 1], 0), corner(stations[i + 1], 1),
        corner(stations[i + 1], 2), corner(stations[i + 1], 3)];
      for (let k = 0; k < 4; k++) {
        out.pushV(c[k], d[k]);                       // chord
        out.pushV(c[k], d[(k + 1) % 4]);             // X, one way
        out.pushV(c[(k + 1) % 4], d[k]);             // X, the other
      }
    }
    if (floors && floors.indexOf(i) !== -1) {
      out.pushV(c[0], c[2]);
      out.pushV(c[1], c[3]);
    }
  }
}

// --- SPHERE: a geodesic dome ------------------------------------------------
// "Subdivided three times" is read as FREQUENCY 3 - each icosahedron face split
// into 9 - and not as three recursive halvings. Frequency 3 gives 270 edges
// averaging 1.19 units, i.e. 321 units of edge and a density of 50, dead centre
// of the band. Three recursive halvings would give 1920 edges and a density of
// 18: the edges would be drawn with nine points each and the dome would read as
// a grey shell rather than as a structure of lit lines.
//
// The full sphere, not a half dome: the whole visualizer is about orbiting and
// traversing, and a dome has a flat open bottom that reads as a mistake the
// moment the camera drops below its rim.
function makeSphere(coarse) {
  const t = (1 + Math.sqrt(5)) / 2;
  const V = [
    [-1, t, 0], [1, t, 0], [-1, -t, 0], [1, -t, 0],
    [0, -1, t], [0, 1, t], [0, -1, -t], [0, 1, -t],
    [t, 0, -1], [t, 0, 1], [-t, 0, -1], [-t, 0, 1]
  ];
  const F = [
    [0, 11, 5], [0, 5, 1], [0, 1, 7], [0, 7, 10], [0, 10, 11],
    [1, 5, 9], [5, 11, 4], [11, 10, 2], [10, 7, 6], [7, 1, 8],
    [3, 9, 4], [3, 4, 2], [3, 2, 6], [3, 6, 8], [3, 8, 9],
    [4, 9, 5], [2, 4, 11], [6, 2, 10], [8, 6, 7], [9, 8, 1]
  ];
  // FREQUENCY 3 at tier 2, 2 at tier 1. The density band is what picks this:
  // frequency 3 over 16 129 points gives 44 points per unit, frequency 3 over
  // tier 1's 9 025 gives 25 - edges drawn thinly enough that the dome starts
  // reading as a dotted shell. Coarsening the structure with the point count is
  // what keeps a tier-1 edge as dense as a tier-2 one.
  const FREQ = coarse ? 2 : 3;
  const verts = [];
  const byKey = new Map();
  const seen = new Set();
  const out = new EdgeList();

  const add = function (x, y, z) {
    const l = Math.sqrt(x * x + y * y + z * z) || 1;
    const p = [x / l * S, y / l * S, z / l * S];
    // Quantised key: the same lattice point is reached from up to five faces.
    const k = Math.round(p[0] * 2048) + ',' + Math.round(p[1] * 2048) + ',' + Math.round(p[2] * 2048);
    let idx = byKey.get(k);
    if (idx === undefined) { idx = verts.length; verts.push(p); byKey.set(k, idx); }
    return idx;
  };
  const link = function (i, j) {
    const key = i < j ? i * 100000 + j : j * 100000 + i;
    if (seen.has(key)) return;
    seen.add(key);
    out.pushV(verts[i], verts[j]);
  };

  for (const f of F) {
    const A = V[f[0]], B = V[f[1]], C = V[f[2]];
    const grid = [];
    for (let i = 0; i <= FREQ; i++) {
      grid.push([]);
      for (let j = 0; j <= FREQ - i; j++) {
        const wa = (FREQ - i - j) / FREQ, wb = i / FREQ, wc = j / FREQ;
        grid[i].push(add(
          A[0] * wa + B[0] * wb + C[0] * wc,
          A[1] * wa + B[1] * wb + C[1] * wc,
          A[2] * wa + B[2] * wb + C[2] * wc
        ));
      }
    }
    for (let i = 0; i < FREQ; i++) {
      for (let j = 0; j < FREQ - i; j++) {
        link(grid[i][j], grid[i + 1][j]);
        link(grid[i][j], grid[i][j + 1]);
        link(grid[i + 1][j], grid[i][j + 1]);
      }
    }
  }
  return {
    edges: out.done(),
    // Latitude bands - the reading a geodesic dome invites.
    zone: function (x, y) { return band(y / S); }
  };
}

// --- TOWER: a square-section lattice tower, six storeys ---------------------
function makeTower(coarse) {
  const out = new EdgeList();
  const LEVELS = coarse ? 5 : 7;     // 6 storeys
  // 3.10 and 1.60, not S and 1.90: a tower's BOTTOM CORNERS are its furthest
  // points, and at S they sat 4.33 from the centre - 27% beyond the sphere's
  // own 3.40, so the tower alone swung out of frame on every orbit where the
  // others did not. Every structure here is kept inside a bounding radius of
  // 3.9 so that one orbit radius frames all six.
  const HEIGHT = 3.20;
  // Wide, because the width is what buys the edge length: a narrower tower of
  // the same height came out at 65 points per unit, above the band, and the
  // storeys read as solid bars rather than as trusses.
  const HALF_BASE = 1.85, HALF_TOP = 1.12;
  const stations = [];
  for (let i = 0; i < LEVELS; i++) {
    const t = i / (LEVELS - 1);
    stations.push({
      p: [0, lerp(-HEIGHT, HEIGHT, t), 0],
      u: [1, 0, 0], v: [0, 0, 1],
      half: lerp(HALF_BASE, HALF_TOP, t)
    });
  }
  // Floor bracing at the base, the middle and the top only: at every level the
  // inside fills with crossings and the tower stops being see-through, which is
  // exactly what the traversal needs it to be.
  trussEdges(out, stations, coarse ? [0, 4] : [0, 3, 6]);
  return {
    edges: out.done(),
    zone: function (x, y) { return band(y / HEIGHT); }   // storeys
  };
}

// --- RING: a fat torus, traversable through its hole ------------------------
function makeRing(coarse) {
  const out = new EdgeList();
  const R = 2.35, r = 1.05;          // R + r = S; the hole is 1.30 across radius
  const MERID = coarse ? 14 : 26, LONGI = coarse ? 7 : 12, SEG = coarse ? 8 : 12;
  const at = function (u, v) {
    const cu = Math.cos(u), su = Math.sin(u);
    const rad = R + r * Math.cos(v);
    return [cu * rad, r * Math.sin(v), su * rad];
  };
  // the meridian circles (the tube's sections)
  for (let i = 0; i < MERID; i++) {
    const u = (i / MERID) * Math.PI * 2;
    for (let k = 0; k < SEG; k++) {
      out.pushV(at(u, (k / SEG) * Math.PI * 2), at(u, ((k + 1) / SEG) * Math.PI * 2));
    }
  }
  // the longitudinal lines running round the ring
  for (let j = 0; j < LONGI; j++) {
    const v = (j / LONGI) * Math.PI * 2;
    for (let k = 0; k < MERID; k++) {
      out.pushV(at((k / MERID) * Math.PI * 2, v), at(((k + 1) / MERID) * Math.PI * 2, v));
    }
  }
  return {
    edges: out.done(),
    // Angle round the ring: five arcs of colour, which is how a ring of drones
    // is always lit.
    zone: function (x, y, z) {
      let a = Math.atan2(z, x) / (Math.PI * 2);
      if (a < 0) a += 1;
      return Math.min(ZONES - 1, Math.floor(a * ZONES));
    }
  };
}

// --- ARCH: a parabolic truss on two piers -----------------------------------
function makeArch(coarse) {
  const out = new EdgeList();
  // Sized by its own worst corner, like the tower: at a half-span of 2.55 and
  // piers reaching y = -S, the bottom outside corner of each pier sat 4.66 from
  // the centre and left the frame long before the rest of the arch did.
  const SPAN = 2.15;                 // half-span
  const APEX = 2.80;
  const SPRING = -0.95;              // where the arch meets the piers
  const FOOT = -2.75;                // where the piers meet the ground
  const SECT = 0.46;                 // arch section half-width
  const RISE = APEX - SPRING;
  const yAt = function (x) { return APEX - (x / SPAN) * (x / SPAN) * RISE; };

  // 14 stations, not 26: at 26 the truss came out at a density of 34, below the
  // band - the same edge budget spread over fewer, longer bays lands at 45.
  const ST = coarse ? 6 : 14;
  const stations = [];
  for (let i = 0; i < ST; i++) {
    const x = lerp(-SPAN, SPAN, i / (ST - 1));
    const dy = -2 * RISE * x / (SPAN * SPAN);
    const l = Math.sqrt(1 + dy * dy);
    stations.push({
      p: [x, yAt(x), 0],
      u: [-dy / l, 1 / l, 0],        // the arch's own normal, in the xy plane
      v: [0, 0, 1],
      half: SECT
    });
  }
  trussEdges(out, stations, null);

  // The two piers, from the ground up to the springing.
  const PIER = 0.50;
  for (const sx of [-1, 1]) {
    const ps = [];
    const LV = coarse ? 3 : 4;
    for (let i = 0; i < LV; i++) {
      ps.push({
        p: [sx * SPAN, lerp(FOOT, SPRING, i / (LV - 1)), 0],
        u: [1, 0, 0], v: [0, 0, 1], half: PIER
      });
    }
    trussEdges(out, ps, [0]);
  }
  return {
    edges: out.done(),
    // BANDS BY HEIGHT: the piers take the bottom colour, the haunches the
    // middle, the crown the top. Bands across the span were the first reading
    // and they measure badly - the two piers are vertical, so a third of the
    // structure's length sits at x = +-SPAN and the two outer bands took 32%
    // each while the middle three shared 12% apiece.
    zone: function (x, y) { return band((y - FOOT) / (APEX + SECT - FOOT) * 2 - 1); }
  };
}

// --- LATTICE: a 5x5x5 cubic net ---------------------------------------------
function makeLattice(coarse) {
  const out = new EdgeList();
  const N = coarse ? 4 : 5;
  // Half 2.25, not S: a cube of half-extent 3.4 has a circumradius of 5.9 and
  // its corners leave the frame on every orbit. At 2.25 the circumradius is 3.9
  // and the diagonal views still fit.
  const H = 2.25;
  const at = function (i, j, k) {
    return [
      lerp(-H, H, i / (N - 1)),
      lerp(-H, H, j / (N - 1)),
      lerp(-H, H, k / (N - 1))
    ];
  };
  for (let i = 0; i < N; i++) {
    for (let j = 0; j < N; j++) {
      for (let k = 0; k < N; k++) {
        if (i + 1 < N) out.pushV(at(i, j, k), at(i + 1, j, k));
        if (j + 1 < N) out.pushV(at(i, j, k), at(i, j + 1, k));
        if (k + 1 < N) out.pushV(at(i, j, k), at(i, j, k + 1));
      }
    }
  }
  return {
    edges: out.done(),
    // DIAGONAL SLABS, not concentric shells. "Distance to the centre" was the
    // first reading, and measured it collapses: a 5-cell lattice has only three
    // Chebyshev shells, so two of the five zones came out empty and one held
    // 73% of the structure. The sum of the coordinates is the coordinate that
    // does divide a cube into five contiguous, populated bands, and a colour
    // front running corner to corner is the most striking thing a cube of
    // lights can do anyway.
    zone: function (x, y, z) { return band((x + y + z) / (3 * H) * 1.7); }
  };
}

// --- SPIRAL: a two-strand helix with rungs ----------------------------------
// THE ONE STRUCTURE WHERE THE DENSITY BAND AND LEGIBILITY GENUINELY CONFLICT,
// and legibility wins. A two-strand helix of this size contains about 105 units
// of edge, so at 16 129 points it runs at 150 per unit - three times the band.
// Every way of manufacturing the missing 200 units was tried and every one of
// them destroys the reading, because they all fill the INTERIOR:
//   three-line cables per strand (0.18 apart)  - too far to fuse, too close to
//                                                separate: a fuzzy tube
//   two-rail ladders with ties (0.56 apart)    - the ties fill the gap and each
//                                                strand becomes a solid ribbon
//   more turns (3 instead of 2)                - pitch 2.13 against a diameter
//                                                of 4.0: the turns overlap from
//                                                every side angle
//   more rungs (8 per turn)                    - 31% of the points on radial
//                                                bars that fill the middle
// The density band exists to serve criterion 1 - the structure must read as an
// object - so where it works against it, it gives way. What is left is what a
// giant double helix should be: two clean strands, four rungs a turn, an axis.
// The brightness is normalised for the density (see _structureIntensity), so
// running three times denser does not make this the brightest structure.
function makeSpiral(coarse) {
  const out = new EdgeList();
  // TWO turns, not three. Pitch against diameter is what decides whether a
  // helix reads as a helix or as a spring: at two turns over this height the
  // pitch is 3.2 against a diameter of 4.8 and you can see between the turns.
  const R = 2.40, H = 6.4, TURNS = 2;
  const SEG = coarse ? 70 : 110;
  const RUNGS = (coarse ? 3 : 4) * TURNS;
  const strand = function (t, off) {
    const th = t * TURNS * Math.PI * 2 + off;
    return [Math.cos(th) * R, (t - 0.5) * H, Math.sin(th) * R];
  };
  for (const off of [0, Math.PI]) {
    for (let i = 0; i < SEG; i++) {
      out.pushV(strand(i / SEG, off), strand((i + 1) / SEG, off));
    }
  }
  for (let i = 0; i < RUNGS; i++) {
    const t = (i + 0.5) / RUNGS;
    out.pushV(strand(t, 0), strand(t, Math.PI));
  }
  // The axis: without it the two strands read as two separate ribbons from
  // side on, and the double helix stops being one object.
  const AX = coarse ? 14 : 24;
  for (let i = 0; i < AX; i++) {
    out.push(0, lerp(-H / 2, H / 2, i / AX), 0, 0, lerp(-H / 2, H / 2, (i + 1) / AX), 0);
  }
  return {
    edges: out.done(),
    zone: function (x, y) { return band(y / (H / 2)); }
  };
}

const BUILDERS = {
  sphere: makeSphere, tower: makeTower, ring: makeRing,
  arch: makeArch, lattice: makeLattice, spiral: makeSpiral
};
// progress -> which structure. The brief's reading: the simple, recognisable
// ones open and close the track, the dense ones hold the middle.
const COMPLEXITY = {
  sphere: 0.12, arch: 0.26, tower: 0.46, ring: 0.54, spiral: 0.80, lattice: 0.92
};

// ============================================================================
// Simulation shaders - highp, positions integrate for the length of a set.
// ============================================================================
const simVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

// THE INSTABILITY, as value noise rather than as a sine. A sine is a machine
// oscillating; a drone holding station against wind and its own control loop
// wanders irregularly, never repeating the same excursion twice. Three
// independent lattice-noise samples, one per axis, read from seeds far enough
// apart that the axes are uncorrelated.
const WOBBLE_CHUNK = `
  float h31(vec3 p) {
    return fract(sin(dot(p, vec3(12.9898, 78.233, 37.719))) * 43758.5453);
  }
  float vnoise(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    vec3 w = f * f * (3.0 - 2.0 * f);
    float n000 = h31(i);
    float n100 = h31(i + vec3(1.0, 0.0, 0.0));
    float n010 = h31(i + vec3(0.0, 1.0, 0.0));
    float n110 = h31(i + vec3(1.0, 1.0, 0.0));
    float n001 = h31(i + vec3(0.0, 0.0, 1.0));
    float n101 = h31(i + vec3(1.0, 0.0, 1.0));
    float n011 = h31(i + vec3(0.0, 1.0, 1.0));
    float n111 = h31(i + vec3(1.0, 1.0, 1.0));
    return mix(
      mix(mix(n000, n100, w.x), mix(n010, n110, w.x), w.y),
      mix(mix(n001, n101, w.x), mix(n011, n111, w.x), w.y), w.z) * 2.0 - 1.0;
  }
  // seed: this machine's own identity, 0..1. rate: its own frequency.
  vec3 wobble(float seed, float rate, float t) {
    vec3 b = vec3(seed * 37.0, seed * 61.0 + 11.0, seed * 83.0 + 29.0);
    float tt = t * rate;
    return vec3(
      vnoise(b + vec3(tt, 0.0, 0.0)),
      vnoise(b.yzx + vec3(0.0, tt, 0.0)),
      vnoise(b.zxy + vec3(0.0, 0.0, tt))
    );
  }
`;

const velocityFragmentShader = `
  precision highp float;

  uniform sampler2D uPos;
  uniform sampler2D uVel;
  uniform sampler2D uTargets;
  uniform sampler2D uAttr;
  uniform float uDt;
  uniform float uTime;
  uniform float uShapeTime;
  uniform float uAttract;
  uniform float uDamping;
  uniform float uMaxSpeed;
  uniform float uWobbleAmp;
  uniform float uWobbleRate;
  uniform float uExplode;

  varying vec2 vUv;
  ${WOBBLE_CHUNK}

  void main() {
    vec3 pos = texture2D(uPos, vUv).xyz;
    vec3 vel = texture2D(uVel, vUv).xyz;
    vec4 T = texture2D(uTargets, vUv);
    vec4 A = texture2D(uAttr, vUv);

    // The staggered start, as in Swarm: T.w is this point's rank in the sorted
    // assignment scaled to seconds, so a structure change resolves as a wave
    // sweeping through the swarm instead of the whole thing snapping at once.
    float started = step(T.w, uShapeTime);

    // A.x = this machine's seed, A.y = its own frequency multiplier,
    // A.z = its station-keeping error (worse at the ends of an edge, and four
    // times worse for the 1% that are badly calibrated).
    vec3 hold = T.xyz + wobble(A.x, uWobbleRate * A.y, uTime) * (uWobbleAmp * A.z);

    vec3 accel = (hold - pos) * (uAttract * started) - vel * uDamping;

    // The drop: everything flies apart radially, then the spring brings it
    // back. Swarm's explosion, verbatim.
    float r = length(pos);
    accel += (r > 0.001 ? pos / r : vec3(0.0, 1.0, 0.0)) * uExplode;

    vel += accel * uDt;
    float sp = length(vel);
    if (sp > uMaxSpeed) vel *= uMaxSpeed / sp;

    gl_FragColor = vec4(vel, 0.0);
  }
`;

const positionFragmentShader = `
  precision highp float;
  uniform sampler2D uPos;
  uniform sampler2D uVel;
  uniform float uDt;
  varying vec2 vUv;
  void main() {
    gl_FragColor = vec4(texture2D(uPos, vUv).xyz + texture2D(uVel, vUv).xyz * uDt, 1.0);
  }
`;

// ============================================================================
// Render - mediump. On the TV mediump is genuinely fp16 (see the TV notes), so
// nothing here feeds an unbounded value into a hash: the per-point brightness
// jitter is READ from the attribute texture rather than hashed in the shader,
// and every exp() argument is bounded by construction.
// ============================================================================
const pointsVertexShader = `
  precision mediump float;

  uniform sampler2D uPosTex;
  uniform sampler2D uTargets;
  uniform sampler2D uAttrTex;
  uniform sampler2D uZoneTex;
  uniform float uSizeScale;
  uniform float uSizeMax;
  uniform float uPixelRatio;
  uniform float uIntensity;
  uniform float uSparkle;
  uniform vec3 uWaveAxis;
  uniform float uWavePhase;
  uniform float uWaveGain;
  uniform float uFogK;
  uniform float uZoneDebug;

  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  varying vec3 vColor;
  varying float vAlpha;
  varying float vSoft;

  void main() {
    vec2 tUv = position.xy;
    vec4 P = texture2D(uPosTex, tUv);
    vec4 T = texture2D(uTargets, tUv);
    vec4 A = texture2D(uAttrTex, tUv);

    vec4 mv = modelViewMatrix * vec4(P.xyz, 1.0);
    gl_Position = projectionMatrix * mv;

    float depth = max(-mv.z, 0.05);
    float px = clamp(uSizeScale / depth, ${POINT_SIZE_MIN.toFixed(1)}, uSizeMax);
    gl_PointSize = px * uPixelRatio;
    // Big points are DEFOCUSED, not just big: a light a metre from the lens is
    // a soft disc, and this is most of what makes the traversal feel like a
    // traversal rather than a zoom.
    vSoft = smoothstep(13.0, ${THROUGH_SIZE_MAX.toFixed(1)}, px);

    // COLOUR BY ZONE. A.w is this point's zone, looked up in an 8x1 texture of
    // the current zone colours - contiguous blocks with hard borders, which is
    // what reads as "drones" instead of "particles".
    vec3 zc = texture2D(uZoneTex, vec2((A.w + 0.5) / ${ZONE_TEX_W.toFixed(1)}, 0.5)).rgb;

    // The swarm is never uniform: every machine carries its own slight
    // brightness offset, generated on the CPU and carried in A.x.
    float jitter = fract(A.x * 7.0);
    float bright = 0.86 + 0.30 * jitter;

    // THE BEAT WAVE: a band of light crossing the structure along one axis,
    // a different axis every beat. Projected on the point's own position, so
    // it is genuinely a plane sweeping through the volume.
    float proj = dot(P.xyz, uWaveAxis) * ${(0.5 / S).toFixed(4)} + 0.5;
    float d = fract(uWavePhase - proj + 1.0);
    bright += exp(-d * d * 70.0) * uWaveGain;

    // In flight = brighter, so a metamorphosis is carried by the points that
    // are actually moving. The instability keeps a small permanent offset, so
    // the ramp starts above it.
    float dist = length(T.xyz - P.xyz);
    bright += smoothstep(0.14, 0.7, dist) * 0.55;
    bright += uSparkle * jitter;

    // The haze. The far side of the structure is dimmed but never lost -
    // seeing the back through the front is what gives the thing volume.
    float atmo = mix(${FOG_FLOOR.toFixed(2)}, 1.0,
                     exp(-max(0.0, depth - ${FOG_REF.toFixed(1)}) * uFogK));

    vec2 ndc = gl_Position.xy / max(gl_Position.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float safe = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));

    vec3 lit = zc * bright * atmo * uIntensity;
    // ?airzones=1: flat zone colour, nothing else, to check the borders.
    vColor = mix(lit, zc * 0.9, uZoneDebug);
    vAlpha = safe;
  }
`;

const pointsFragmentShader = `
  precision mediump float;
  varying vec3 vColor;
  varying float vAlpha;
  varying float vSoft;
  void main() {
    vec2 d = gl_PointCoord - 0.5;
    float r2 = dot(d, d);
    if (r2 > 0.25) discard;
    // A crisp core with a small halo - and the core spreads out and dims as
    // the point grows, which is the defocus.
    float core = 1.0 - smoothstep(0.0, mix(0.045, 0.235, vSoft), r2);
    float halo = 1.0 - smoothstep(0.0, 0.25, r2);
    vec3 col = vColor * (halo * 0.5 + core * mix(1.0, 0.40, vSoft));
    // AdditiveBlending is (SRC_ALPHA, ONE): all the intensity lives in rgb.
    gl_FragColor = vec4(col * vAlpha, 1.0);
  }
`;

// --- stars ------------------------------------------------------------------
// Fixed, very fine, and seen THROUGH the structure. They do two jobs: they give
// the scene a scale the structure alone cannot, and because they stay visible
// behind the points they prove the structure is translucent.
const starVertexShader = `
  precision mediump float;
  attribute float aMag;
  uniform float uPixelRatio;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying float vMag;
  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = (1.0 + aMag * 1.1) * uPixelRatio;
    vec2 ndc = gl_Position.xy / max(gl_Position.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    vMag = (0.22 + aMag * 0.5) * mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));
  }
`;
const starFragmentShader = `
  precision mediump float;
  varying float vMag;
  void main() {
    vec2 d = gl_PointCoord - 0.5;
    if (dot(d, d) > 0.25) discard;
    gl_FragColor = vec4(vec3(0.72, 0.80, 1.0) * vMag, 1.0);
  }
`;

// --- backdrop ---------------------------------------------------------------
const bgVertexShader = `
  precision mediump float;
  void main() { gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
// Deep black with an ordered dither. At this level an undithered flat fill
// bands visibly on an 8-bit panel wherever the haze fades the far points out,
// and the TV is exactly where that shows.
const bgFragmentShader = `
  precision mediump float;
  float bayer4(vec2 fc) {
    vec2 p1 = mod(fc, 2.0);
    vec2 p2 = floor(0.5 * mod(fc, 4.0));
    return 4.0 * mix(mix(0.0, 2.0, p1.y), mix(3.0, 1.0, p1.y), p1.x)
         +       mix(mix(0.0, 2.0, p2.y), mix(3.0, 1.0, p2.y), p2.x);
  }
  void main() {
    float d = (bayer4(floor(gl_FragCoord.xy)) / 16.0 - 0.5) / 255.0;
    gl_FragColor = vec4(vec3(0.012) + d, 1.0);
  }
`;

export class AirframeVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    // Clamped, not defaulted: a live downgrade to tier 0 has to land somewhere,
    // and the tier-1 layout is the floor this visualizer has.
    this._tier = TIER[tier] ? tier : (tier < 1 ? 1 : DEFAULT_TIER);
    const shape = urlParam('airshape');
    this._forcedShape = shape && BUILDERS[shape] ? shape : null;
    const cam = urlParam('aircam');
    this._forcedCam = ['orbit', 'approach', 'through', 'retreat'].indexOf(cam) !== -1 ? cam : null;
    this._stable = urlParam('airstable') === '1';
    this._zoneDebug = urlParam('airzones') === '1';
    this._densityDebug = urlParam('airdensity') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const layout = TIER[this._tier];
    this._n = layout.n;
    this._count = this._n * this._n;
    this._names = this._forcedShape ? [this._forcedShape] : layout.structures.slice();

    // --- the structures, generated once ---
    this._structures = {};
    // Tier 1 gets COARSER structures, not just fewer points: keeping the tier-2
    // subdivision at 9 025 points drops the edge density to 25 per unit and the
    // edges stop reading as lines. Coarsened, tier 1 lands back in the band.
    const coarse = this._tier < 2;
    for (const name of this._names) this._structures[name] = BUILDERS[name](coarse);
    this._prepareLengths();
    if (this._densityDebug) this._reportDensity();

    // --- GPU state ---
    const bufOpts = { width: this._n, height: this._n, type: THREE.FloatType, filter: THREE.NearestFilter };
    this._pos = new FeedbackBuffer(bufOpts);
    this._vel = new FeedbackBuffer(bufOpts);
    this._simReady = false;

    // targets: xyz = the position this point holds, w = its start delay.
    this._targetData = new Float32Array(this._count * 4);
    this._targetTex = this._dataTex(this._targetData);
    // attributes: x = seed, y = frequency multiplier, z = station-keeping
    // error, w = zone. Static per structure, uploaded with the targets.
    this._attrData = new Float32Array(this._count * 4);
    this._attrTex = this._dataTex(this._attrData);

    // The zone palette, as an 8x1 texture rather than a uniform array: a
    // vertex-shader array lookup with a non-constant index is legal but
    // fragile across the drivers this runs on, and 32 bytes a bar is nothing.
    this._zoneData = new Uint8Array(ZONE_TEX_W * 4);
    this._zoneTex = new THREE.DataTexture(this._zoneData, ZONE_TEX_W, 1, THREE.RGBAFormat);
    this._zoneTex.minFilter = THREE.NearestFilter;
    this._zoneTex.magFilter = THREE.NearestFilter;
    this._zoneTex.needsUpdate = true;

    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    this._simScene = new THREE.Scene();
    this._simQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial());
    this._simQuad.frustumCulled = false;
    this._simScene.add(this._simQuad);

    this._velUniforms = {
      uPos: { value: null }, uVel: { value: null },
      uTargets: { value: this._targetTex }, uAttr: { value: this._attrTex },
      uDt: { value: FIXED_DT }, uTime: { value: 0 }, uShapeTime: { value: 0 },
      uAttract: { value: ATTRACT }, uDamping: { value: DAMPING },
      uMaxSpeed: { value: MAX_SPEED },
      uWobbleAmp: { value: this._stable ? 0 : WOBBLE_AMP },
      uWobbleRate: { value: WOBBLE_RATE },
      uExplode: { value: 0 }
    };
    this._posUniforms = { uPos: { value: null }, uVel: { value: null }, uDt: { value: FIXED_DT } };
    this._velMaterial = new THREE.ShaderMaterial({
      uniforms: this._velUniforms, vertexShader: simVertexShader,
      fragmentShader: velocityFragmentShader, depthTest: false, depthWrite: false
    });
    this._posMaterial = new THREE.ShaderMaterial({
      uniforms: this._posUniforms, vertexShader: simVertexShader,
      fragmentShader: positionFragmentShader, depthTest: false, depthWrite: false
    });

    // --- the points: one draw call ---
    const geo = new THREE.BufferGeometry();
    const uvs = new Float32Array(this._count * 3);
    for (let y = 0; y < this._n; y++) {
      for (let x = 0; x < this._n; x++) {
        const i = (y * this._n + x) * 3;
        uvs[i] = (x + 0.5) / this._n;
        uvs[i + 1] = (y + 0.5) / this._n;
      }
    }
    geo.setAttribute('position', new THREE.BufferAttribute(uvs, 3));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 100);

    // Fewer points over the same edges means a dimmer structure; holding the
    // apparent brightness exactly would need 1/count, which makes tier 1 read
    // as a different visualizer. The square root splits the difference.
    const tierGain = Math.sqrt(REF_COUNT / this._count);
    this._baseIntensity = BASE_INTENSITY * Math.min(1.5, tierGain);

    this._pointUniforms = Object.assign({}, safeZoneUniforms, {
      uPosTex: { value: null },
      uTargets: { value: this._targetTex },
      uAttrTex: { value: this._attrTex },
      uZoneTex: { value: this._zoneTex },
      uSizeScale: { value: SIZE_SCALE },
      uSizeMax: { value: POINT_SIZE_MAX },
      uPixelRatio: { value: 1 },
      uIntensity: { value: this._baseIntensity },
      uSparkle: { value: 0 },
      uWaveAxis: { value: new THREE.Vector3(0, 1, 0) },
      uWavePhase: { value: 0 },
      uWaveGain: { value: 0.85 },
      uFogK: { value: layout.fog ? FOG_K : FOG_K * 0.35 },
      uZoneDebug: { value: this._zoneDebug ? 1 : 0 }
    });
    this._pointsMat = new THREE.ShaderMaterial({
      uniforms: this._pointUniforms,
      vertexShader: pointsVertexShader,
      fragmentShader: pointsFragmentShader,
      blending: THREE.AdditiveBlending,
      transparent: true, depthTest: false, depthWrite: false
    });
    this._points = new THREE.Points(geo, this._pointsMat);
    this._points.frustumCulled = false;
    const self = this;
    this._points.onBeforeRender = function (renderer) { self._runSim(renderer); };
    scene.add(this._points);

    this._buildStars(scene, layout.stars);
    this._buildBackdrop(scene);

    // --- state ---
    this._time = 0;
    this._shapeTime = 0;
    this._accumulator = 0;
    this._paused = false;
    this._current = null;
    this._currentTargets = null;
    this._pendingName = null;
    this._morphUntil = -1;
    this._lastPhraseIndex = null;
    this._lastBeatIndex = null;
    this._lastBarIndex = null;
    this._structurePhrase = 0;
    this._structTimer = 0;
    this._lastPaletteRef = undefined;
    this._lowSmooth = 0.5;
    this._midSmooth = 0.5;
    this._highSmooth = 0.5;
    this._explodeUntil = -1;
    this._recomposeUntil = -1;
    this._tightenUntil = -1;
    this._pendingDropAt = null;
    this._sizeMax = POINT_SIZE_MAX;
    this._structIntensity = 1;
    this._freeBeat = 0;

    this._palette = [
      new THREE.Color(0x63d2ff), new THREE.Color(0xff5fa8), new THREE.Color(0xffd166),
      new THREE.Color(0x8b6bff), new THREE.Color(0x4dffc3), new THREE.Color(0xff8a4d)
    ];
    this._zoneMap = [];
    for (let z = 0; z < ZONES; z++) this._zoneMap.push(z % this._palette.length);
    this._writeZones();

    this._sortA = new Float64Array(this._count);
    this._sortB = new Float64Array(this._count);

    // --- camera ---
    this._theta = 0.6;
    this._phi = 0.22;
    this._phiTarget = 0.22;
    this._phase = 'orbit';
    this._phaseBeats = 0;
    this._queue = [];
    this._anchorDir = new THREE.Vector3(Math.sin(0.6), 0.22, Math.cos(0.6)).normalize();
    this._anchorRadius = ORBIT_RADIUS;
    this._anchorLook = new THREE.Vector3(0, 0, 0);
    this._throughAxis = new THREE.Vector3(1, 0, 0);
    this._throughSide = new THREE.Vector3(0, 0, 1);
    this._camPos = new THREE.Vector3();
    this._camLook = new THREE.Vector3();
    this._radiusScale = 1;
    this._radiusScaleTarget = 1;

    // The opening structure is assigned synchronously - the seed reads it.
    this._activate(this._names[0], true);
    this._placeCamera(0, 1 / 60);
  }

  _dataTex(data) {
    const t = new THREE.DataTexture(data, this._n, this._n, THREE.RGBAFormat, THREE.FloatType);
    t.minFilter = THREE.NearestFilter;
    t.magFilter = THREE.NearestFilter;
    t.needsUpdate = true;
    return t;
  }

  // ------------------------------------------------------------- sampling --
  _prepareLengths() {
    for (const name in this._structures) {
      const st = this._structures[name];
      const e = st.edges;
      const m = e.length / 6;
      const cum = new Float64Array(m);
      let total = 0;
      for (let i = 0; i < m; i++) {
        const o = i * 6;
        const dx = e[o + 3] - e[o], dy = e[o + 4] - e[o + 1], dz = e[o + 5] - e[o + 2];
        total += Math.sqrt(dx * dx + dy * dy + dz * dz);
        cum[i] = total;
      }
      st.cum = cum;
      st.length = total;
      st.density = this._count / Math.max(1e-6, total);
      st.intensity = Math.max(INTENSITY_CLAMP[0],
        Math.min(INTENSITY_CLAMP[1], Math.sqrt(REF_DENSITY / st.density)));
    }
  }

  _reportDensity() {
    for (const name in this._structures) {
      const st = this._structures[name];
      const d = this._count / st.length;
      const ok = d >= EDGE_DENSITY_MIN && d <= EDGE_DENSITY_MAX ? 'ok' : 'OUT OF BAND';
      // eslint-disable-next-line no-console
      console.log('[airframe] ' + name + ': ' + (st.edges.length / 6) + ' edges, '
        + st.length.toFixed(1) + ' units, ' + d.toFixed(1) + ' points/unit (' + ok + ')'
        + ', intensity x' + st.intensity.toFixed(2));
    }
  }

  // Draws N points along the edges, density proportional to length. Stratified
  // inverse-CDF with a single walking pointer - the draws are generated already
  // increasing, so the cumulative table is walked ONCE instead of N binary
  // searches, and stratification also spreads the points more evenly along an
  // edge than independent draws would.
  _sample(st) {
    const N = this._count;
    const e = st.edges, cum = st.cum, m = cum.length;
    const pts = new Float32Array(N * 3);
    const att = new Float32Array(N * 4);
    let walk = 0;
    for (let k = 0; k < N; k++) {
      const s = ((k + Math.random()) / N) * st.length;
      while (walk < m - 1 && cum[walk] < s) walk++;
      const o = walk * 6;
      const prev = walk > 0 ? cum[walk - 1] : 0;
      const len = Math.max(1e-6, cum[walk] - prev);
      const t = clamp01((s - prev) / len);
      const x = lerp(e[o], e[o + 3], t);
      const y = lerp(e[o + 1], e[o + 4], t);
      const z = lerp(e[o + 2], e[o + 5], t);
      pts[k * 3] = x; pts[k * 3 + 1] = y; pts[k * 3 + 2] = z;

      // THE ENDS OF AN EDGE HOLD WORSE. Every node of the structure therefore
      // has a small cluster of jitterier machines around it, which is what a
      // photograph of a real show looks like: the corners shimmer.
      const endness = Math.abs(2 * t - 1);
      const miscal = Math.random() < MISCAL_FRACTION ? MISCAL_GAIN : 1;
      att[k * 4] = Math.random();                       // seed / brightness
      att[k * 4 + 1] = 0.55 + Math.random() * 1.10;     // own frequency
      att[k * 4 + 2] = (0.55 + 0.95 * endness * endness) * miscal;
      att[k * 4 + 3] = st.zone(x, y, z);
    }
    return { pts: pts, att: att };
  }

  // ------------------------------------------------------------ assignment --
  // Swarm's sorted assignment, unchanged in principle and re-keyed for a
  // standing 3D object: azimuth about the vertical, then height. Both sets are
  // sorted by the same key and paired index for index, which approximates
  // optimal transport for the cost of two sorts - near points get near targets
  // and the structure reorganises in formation instead of as sixteen thousand
  // crossing trajectories.
  _assign(pts, att) {
    const N = this._count;
    const ref = this._currentTargets;
    const A = this._sortA, B = this._sortB;
    const key = function (x, y, z) {
      let a = Math.atan2(z, x) / (Math.PI * 2);
      if (a < 0) a += 1;
      const h = clamp01(y / (2 * S) + 0.5);
      return Math.floor(a * 1e6) * 1000 + Math.floor(h * 999);
    };
    for (let i = 0; i < N; i++) {
      // The key half is an exact multiple of 1e5 so the index survives the
      // round trip; the whole thing stays under 2^53.
      A[i] = ref ? key(ref[i * 3], ref[i * 3 + 1], ref[i * 3 + 2]) * 1e5 + i : i * 1e5 + i;
      B[i] = key(pts[i * 3], pts[i * 3 + 1], pts[i * 3 + 2]) * 1e5 + i;
    }
    A.sort(); B.sort();

    const td = this._targetData, ad = this._attrData;
    const stagger = STAGGER_SPAN_S / N;
    for (let k = 0; k < N; k++) {
      const pi = A[k] % 1e5;        // the machine
      const ti = B[k] % 1e5;        // the position it takes
      const o = pi * 4, so = ti * 4;
      td[o] = pts[ti * 3];
      td[o + 1] = pts[ti * 3 + 1];
      td[o + 2] = pts[ti * 3 + 2];
      td[o + 3] = k * stagger;      // the rank IS the delay
      ad[o] = att[so]; ad[o + 1] = att[so + 1];
      ad[o + 2] = att[so + 2]; ad[o + 3] = att[so + 3];
    }
    this._targetTex.needsUpdate = true;
    this._attrTex.needsUpdate = true;

    if (!this._currentTargets) this._currentTargets = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      const o = i * 4;
      this._currentTargets[i * 3] = td[o];
      this._currentTargets[i * 3 + 1] = td[o + 1];
      this._currentTargets[i * 3 + 2] = td[o + 2];
    }
  }

  // Sampling plus two sorts is a few milliseconds at 16k - not a frame spike,
  // but not free either, and it lands at exactly the moment the camera is
  // pulling back to show the change. Deferred to an idle callback, as the brief
  // asks; only the opening structure is assigned inline, because the seed needs
  // it before the first frame.
  _activate(name, immediate) {
    const st = this._structures[name];
    if (!st || name === this._pendingName) return;
    this._pendingName = name;
    const self = this;
    const run = function () {
      if (self._disposed || self._pendingName !== name) return;
      const s = self._sample(st);
      self._assign(s.pts, s.att);
      self._current = name;
      self._structIntensity = st.intensity;
      self._shapeTime = 0;
      self._pendingName = null;
      self._morphUntil = self._time + MORPH_S;
      // The camera pulls back to show the whole metamorphosis, and any
      // approach or traversal queued behind it is dropped: flying through a
      // structure while it is dissolving shows nothing.
      self._queue.length = 0;
      if (!self._forcedCam) self._startPhase('retreat');
    };
    if (immediate) run(); else idle(run);
  }

  _nextStructure() {
    if (this._names.length < 2) return this._names[0];
    // progress -> complexity: the simple shapes open and close, the dense ones
    // hold the middle. Jittered, so a track does not always show the same two.
    const want = clamp01(this._progress != null ? Math.sin(Math.PI * this._progress) : 0.5)
      + (Math.random() - 0.5) * 0.22;
    let best = null, bestD = 1e9;
    for (const name of this._names) {
      if (name === this._current) continue;
      const d = Math.abs((COMPLEXITY[name] != null ? COMPLEXITY[name] : 0.5) - want);
      if (d < bestD) { bestD = d; best = name; }
    }
    return best || this._names[0];
  }

  // ----------------------------------------------------------------- zones --
  _writeZones() {
    for (let z = 0; z < ZONE_TEX_W; z++) {
      const c = this._palette[this._zoneMap[Math.min(z, ZONES - 1)] % this._palette.length];
      this._zoneData[z * 4] = Math.round(clamp01(c.r) * 255);
      this._zoneData[z * 4 + 1] = Math.round(clamp01(c.g) * 255);
      this._zoneData[z * 4 + 2] = Math.round(clamp01(c.b) * 255);
      this._zoneData[z * 4 + 3] = 255;
    }
    this._zoneTex.needsUpdate = true;
  }

  // On a beat: one zone takes a different colour. Instantly, not faded - a
  // drone show changes a block of colour on the beat and the hardness of the
  // change is half the effect.
  _recolourOne(beatIndex) {
    const z = ((beatIndex % ZONES) + ZONES) % ZONES;
    const n = this._palette.length;
    if (n < 2) return;
    // NEVER THE SAME COLOUR AS EITHER NEIGHBOUR. Drawing freely from six
    // colours for five zones puts two identical bands next to each other about
    // a third of the time, and two adjacent bands of one colour are not two
    // zones with a hard border - they are one zone. The border is the whole
    // point of colouring by zone, so it is enforced rather than hoped for.
    const banned = [this._zoneMap[z], this._zoneMap[(z + ZONES - 1) % ZONES], this._zoneMap[(z + 1) % ZONES]];
    const free = [];
    for (let i = 0; i < n; i++) if (banned.indexOf(i) === -1) free.push(i);
    if (!free.length) return;
    this._zoneMap[z] = free[(Math.random() * free.length) | 0];
    this._writeZones();
  }

  // On a bar: every zone hands its colour to the next one round.
  _permuteZones() {
    const first = this._zoneMap[0];
    for (let z = 0; z < ZONES - 1; z++) this._zoneMap[z] = this._zoneMap[z + 1];
    this._zoneMap[ZONES - 1] = first;
    this._writeZones();
  }

  // ---------------------------------------------------------------- camera --
  // THE TRAJECTORY IS CONTINUOUS BY CONSTRUCTION, not by cross-fading two
  // curves. Every phase is re-anchored at its own start to the camera's current
  // direction, radius and look point, and each phase's curve is written so that
  // its value at u = 0 is exactly that anchor. There is therefore nothing to
  // blend: position and look are C0 by identity and near-C1 because every
  // interpolation runs through a quintic ease.
  _startPhase(name) {
    this._phase = name;
    this._phaseBeats = 0;
    const p = this._camPos.lengthSq() > 1e-6 ? this._camPos : new THREE.Vector3(0, 0, ORBIT_RADIUS);
    this._anchorRadius = p.length();
    this._anchorDir.copy(p).normalize();
    this._anchorLook.copy(this._camLook);
    // Orbit resumes from wherever the camera actually is, including straight
    // out of a traversal on the far side.
    this._theta = Math.atan2(this._anchorDir.x, this._anchorDir.z);
    this._phi = Math.asin(Math.max(-1, Math.min(1, this._anchorDir.y)));
    if (name === 'orbit') {
      this._phiTarget = lerp(PHI_MIN, PHI_MAX, Math.random());
    } else if (name === 'through') {
      this._throughAxis.copy(this._anchorDir);
      const up = new THREE.Vector3(0, 1, 0);
      this._throughSide.crossVectors(this._throughAxis, up);
      if (this._throughSide.lengthSq() < 1e-4) this._throughSide.set(1, 0, 0);
      this._throughSide.normalize();
    }
  }

  _phaseLength(name) {
    if (name === 'approach') return APPROACH_BEATS;
    if (name === 'through') return THROUGH_BEATS;
    if (name === 'retreat') return RETREAT_BEATS;
    return ORBIT_BEATS;
  }

  _placeCamera(dBeat, dt) {
    this._phaseBeats += dBeat;
    const len = this._phaseLength(this._phase);
    if (this._phaseBeats >= len) {
      if (this._forcedCam) {
        this._startPhase(this._forcedCam);
      } else {
        this._startPhase(this._queue.shift() || 'orbit');
      }
    }
    const u = clamp01(this._phaseBeats / this._phaseLength(this._phase));
    this._phaseU = u;

    // The elevation drifts on its own, eased, so it is continuous across
    // phases whatever else happens.
    this._phi += (this._phiTarget - this._phi) * (1 - Math.exp(-dt / PHI_TAU));
    // While the structure is metamorphosing the whole trajectory is pushed out
    // so the change is seen entire.
    this._radiusScale += (this._radiusScaleTarget - this._radiusScale) * (1 - Math.exp(-dt / 0.55));

    const pos = this._camPos, look = this._camLook;
    if (this._phase === 'orbit') {
      // The radius eases from wherever the previous phase left it to the orbit
      // radius over the first four beats - a retreat ends at 2.4 S and an
      // approach at 1.02 S, and neither may snap.
      const e = smoother(Math.min(1, this._phaseBeats / ORBIT_SETTLE_BEATS));
      const r = lerp(this._anchorRadius, ORBIT_RADIUS * this._radiusScale, e);
      this._sph(pos, this._theta, this._phi, r);
      look.set(0, 0, 0);
    } else if (this._phase === 'approach') {
      const e = smoother(u);
      const r = lerp(this._anchorRadius, APPROACH_RADIUS, e);
      // Levelling off as it closes in: at grazing distance a steep angle looks
      // like a crash rather than a pass.
      this._sph(pos, this._theta, this._phi * (1 - 0.35 * e), r);
      look.set(0, 0, 0);
    } else if (this._phase === 'through') {
      // THE SIGNATURE MOVE. A straight chord from the near surface, through the
      // middle, out the far side - with a small lateral and vertical bow so it
      // is a flight and not a dolly. The look point is far ahead along the same
      // axis, which is the same direction the approach was already looking in,
      // so the handover is exact.
      const e = smoother(u);
      const s = lerp(this._anchorRadius, -THROUGH_HALF, e);
      const bow = Math.sin(Math.PI * u);
      pos.copy(this._throughAxis).multiplyScalar(s)
        .addScaledVector(this._throughSide, 0.16 * S * bow);
      pos.y += 0.07 * S * Math.sin(Math.PI * 2 * u);
      look.copy(this._throughAxis).multiplyScalar(-2.6 * S);
      // THE TURN BACK, begun while still inside. Flying out of the far side
      // still looking forward leaves the structure entirely behind the camera
      // and the shot ends on empty sky. The look sweeps round to the centre
      // over the last quarter - which starts with the camera still inside, so
      // the swing happens THROUGH the structure - and it ends aimed at the
      // centre, which is exactly where the retreat wants to begin.
      const back = smoother((u - 0.72) / 0.28);
      if (back > 0) {
        look.lerp(_origin, back);
        look.addScaledVector(this._throughSide, 1.3 * S * Math.sin(Math.PI * back));
      }
    } else {
      // RETREAT. It keeps flying out along the axis it left on while turning
      // back to face the structure. The look point sweeps from far ahead round
      // to the centre on a bowed path - interpolating it straight would walk it
      // through the camera's own position and the framing would tumble.
      const e = smoother(u);
      const r = lerp(this._anchorRadius, RETREAT_RADIUS * this._radiusScale, e);
      this._sph(pos, this._theta, this._phi, r);
      const turn = smoother((u - 0.12) / 0.80);
      const bowAmp = Math.min(1, this._anchorLook.length() / (2.6 * S)) * 2.4 * S;
      look.copy(this._anchorLook).multiplyScalar(1 - turn);
      if (bowAmp > 0.01) look.addScaledVector(this._throughSide, bowAmp * Math.sin(Math.PI * turn));
    }

    const c = this._camera;
    c.position.copy(pos);
    c.lookAt(look);
  }

  _sph(out, theta, phi, r) {
    const cp = Math.cos(phi);
    out.set(Math.sin(theta) * cp * r, Math.sin(phi) * r, Math.cos(theta) * cp * r);
  }

  // ------------------------------------------------------------ background --
  _buildStars(scene, count) {
    const geo = new THREE.BufferGeometry();
    const p = new Float32Array(count * 3);
    const mag = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      const y = 1 - (i / Math.max(1, count - 1)) * 2;
      const rr = Math.sqrt(Math.max(0, 1 - y * y));
      const th = Math.PI * (3 - Math.sqrt(5)) * i + Math.random() * 0.4;
      const R = lerp(STAR_RADIUS_MIN, STAR_RADIUS_MAX, Math.random());
      p[i * 3] = Math.cos(th) * rr * R;
      p[i * 3 + 1] = y * R;
      p[i * 3 + 2] = Math.sin(th) * rr * R;
      mag[i] = Math.pow(Math.random(), 2.2);   // a few bright, most very faint
    }
    geo.setAttribute('position', new THREE.BufferAttribute(p, 3));
    geo.setAttribute('aMag', new THREE.BufferAttribute(mag, 1));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), STAR_RADIUS_MAX * 1.2);
    this._starUniforms = Object.assign({}, safeZoneUniforms, { uPixelRatio: { value: 1 } });
    this._starMat = new THREE.ShaderMaterial({
      uniforms: this._starUniforms,
      vertexShader: starVertexShader,
      fragmentShader: starFragmentShader,
      blending: THREE.AdditiveBlending,
      transparent: true, depthTest: false, depthWrite: false
    });
    this._stars = new THREE.Points(geo, this._starMat);
    this._stars.frustumCulled = false;
    this._stars.renderOrder = -900;
    scene.add(this._stars);
  }

  _buildBackdrop(scene) {
    this._bgMat = new THREE.ShaderMaterial({
      uniforms: {}, vertexShader: bgVertexShader, fragmentShader: bgFragmentShader,
      depthTest: false, depthWrite: false
    });
    this._bg = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._bgMat);
    this._bg.frustumCulled = false;
    this._bg.renderOrder = -1000;
    scene.add(this._bg);
  }

  // ------------------------------------------------------------ simulation --
  _seed(renderer) {
    const N = this._count;
    const pos = new Float32Array(N * 4);
    // Seeded on a wide shell rather than on the structure: the opening four
    // seconds are the swarm ARRIVING and assembling the first structure, which
    // is the right way for this visualizer to begin.
    for (let i = 0; i < N; i++) {
      let x, y, z, d;
      do {
        x = Math.random() * 2 - 1; y = Math.random() * 2 - 1; z = Math.random() * 2 - 1;
        d = x * x + y * y + z * z;
      } while (d > 1 || d < 1e-6);
      const t = (2.0 * S) / Math.sqrt(d);
      pos[i * 4] = x * t; pos[i * 4 + 1] = y * t; pos[i * 4 + 2] = z * t; pos[i * 4 + 3] = 1;
    }
    const tex = new THREE.DataTexture(pos, this._n, this._n, THREE.RGBAFormat, THREE.FloatType);
    tex.needsUpdate = true;
    const copy = new THREE.ShaderMaterial({
      uniforms: { uTex: { value: tex } }, vertexShader: simVertexShader,
      fragmentShader: 'precision highp float; uniform sampler2D uTex; varying vec2 vUv; void main(){ gl_FragColor = texture2D(uTex, vUv); }',
      depthTest: false, depthWrite: false
    });
    this._simQuad.material = copy;
    for (const rt of [this._pos.read, this._pos.write]) {
      renderer.setRenderTarget(rt);
      renderer.render(this._simScene, this._ortho);
    }
    const zero = new THREE.DataTexture(new Float32Array(N * 4), this._n, this._n, THREE.RGBAFormat, THREE.FloatType);
    zero.needsUpdate = true;
    copy.uniforms.uTex.value = zero;
    for (const rt of [this._vel.read, this._vel.write]) {
      renderer.setRenderTarget(rt);
      renderer.render(this._simScene, this._ortho);
    }
    copy.dispose(); tex.dispose(); zero.dispose();
  }

  _substep(renderer) {
    this._simQuad.material = this._velMaterial;
    this._velUniforms.uPos.value = this._pos.read.texture;
    this._velUniforms.uVel.value = this._vel.read.texture;
    renderer.setRenderTarget(this._vel.write);
    renderer.render(this._simScene, this._ortho);
    this._vel.swap();

    this._simQuad.material = this._posMaterial;
    this._posUniforms.uPos.value = this._pos.read.texture;
    this._posUniforms.uVel.value = this._vel.read.texture;
    renderer.setRenderTarget(this._pos.write);
    renderer.render(this._simScene, this._ortho);
    this._pos.swap();
  }

  _runSim(renderer) {
    if (!this._simReady) {
      this._pos.ensureSize(renderer);
      this._vel.ensureSize(renderer);
      this._seed(renderer);
      this._simReady = true;
      const pr = renderer.getPixelRatio();
      this._pointUniforms.uPixelRatio.value = pr;
      this._starUniforms.uPixelRatio.value = pr;
    }
    if (this._paused) return;

    const prev = renderer.getRenderTarget();
    const prevAuto = renderer.autoClear;
    renderer.autoClear = false;

    let steps = 0;
    while (this._accumulator >= FIXED_DT && steps < MAX_SUBSTEPS) {
      this._velUniforms.uTime.value = this._time - this._accumulator;
      this._velUniforms.uShapeTime.value = this._shapeTime - this._accumulator;
      this._substep(renderer);
      this._accumulator -= FIXED_DT;
      steps++;
    }
    // Surplus beyond the budget is dropped, never integrated: a variable dt
    // would make the swarm behave differently at 30 and 60 fps.
    if (this._accumulator > FIXED_DT * MAX_SUBSTEPS) this._accumulator = FIXED_DT * MAX_SUBSTEPS;

    renderer.setRenderTarget(prev);
    renderer.autoClear = prevAuto;
    this._pointUniforms.uPosTex.value = this._pos.read.texture;
  }

  // ---------------------------------------------------------------- update --
  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;      // freezes the simulation AND the camera
    this._time += deltaTime;
    this._shapeTime += deltaTime;
    this._accumulator += deltaTime;

    const director = profile.director;
    const bands = director && director.bands;
    const low = bands && bands.low != null ? clamp01(bands.low) : 0.5;
    const mid = bands && bands.mid != null ? clamp01(bands.mid) : 0.5;
    const high = bands && bands.high != null ? clamp01(bands.high) : 0.5;
    const k = 1 - Math.exp(-deltaTime / 0.18);
    this._lowSmooth += (low - this._lowSmooth) * k;
    this._midSmooth += (mid - this._midSmooth) * k;
    this._highSmooth += (high - this._highSmooth) * k;

    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_GRID_BPM;
    const beatS = 60 / bpm;
    this._progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const vivid = profile.artworkPalette && profile.artworkPalette.vividColors;
      if (vivid && vivid.length) {
        // THE PALETTE IS SIZED TO WHAT THE COVER ACTUALLY GIVES, not padded to
        // a fixed six. artwork-palette returns five vivid colours, and cycling
        // them into six slots makes slots 0 and 5 the same colour - at which
        // point _recolourOne's neighbour guard, which compares INDICES, can
        // still put that one colour in two adjacent zones and merge them.
        // Matching the lengths makes an index guard a colour guard.
        const n = Math.max(3, Math.min(6, vivid.length));
        while (this._palette.length > n) this._palette.pop();
        while (this._palette.length < n) this._palette.push(new THREE.Color());
        for (let i = 0; i < n; i++) this._palette[i].set(vivid[i]);
        for (let z = 0; z < ZONES; z++) this._zoneMap[z] = this._zoneMap[z] % n;
        this._writeZones();
      }
    }

    // --- beat / bar / phrase events ---
    // With no beat grid (a radio stream, or the first seconds of any track) the
    // whole event layer would sit still, so a free-running clock stands in.
    const haveGrid = profile.beatIndex != null;
    let beatIndex, beatPhase, barIndex, phraseIndex;
    if (haveGrid) {
      beatIndex = profile.beatIndex; beatPhase = profile.beatPhase;
      barIndex = profile.barIndex; phraseIndex = profile.phraseIndex;
    } else {
      this._freeBeat += deltaTime / beatS;
      beatIndex = Math.floor(this._freeBeat);
      beatPhase = this._freeBeat - beatIndex;
      barIndex = Math.floor(this._freeBeat / 4);
      phraseIndex = Math.floor(this._freeBeat / 16);
    }

    if (beatIndex !== this._lastBeatIndex) {
      if (this._lastBeatIndex != null) {
        // A DIFFERENT AXIS EVERY BEAT. The golden angle in both spherical
        // coordinates, so consecutive beats never sweep the same plane and the
        // sequence never repeats a cycle you could count.
        const a = beatIndex * 2.39996;
        const b = beatIndex * 1.11729;
        const cp = Math.cos(b * 0.5);
        this._pointUniforms.uWaveAxis.value
          .set(Math.cos(a) * cp, Math.sin(b * 0.5), Math.sin(a) * cp).normalize();
        this._recolourOne(beatIndex);
      }
      this._lastBeatIndex = beatIndex;
    }
    if (barIndex !== this._lastBarIndex) {
      if (this._lastBarIndex != null) this._permuteZones();
      this._lastBarIndex = barIndex;
    }
    this._pointUniforms.uWavePhase.value = beatPhase != null ? beatPhase : 0;

    // --- the camera's own phrase decisions ---
    if (phraseIndex !== this._lastPhraseIndex) {
      if (this._lastPhraseIndex != null) {
        this._structurePhrase++;
        if (!this._forcedCam && !this._queue.length && this._time > this._morphUntil) {
          // Orbit, or the triptych: approach, through, retreat. The traversal
          // is the thing this visualizer exists for, so it is weighted up
          // through the middle of the track where the energy is.
          const wantThrough = 0.34 + 0.40 * Math.sin(Math.PI * this._progress);
          if (Math.random() < wantThrough) {
            this._queue.push('approach', 'through', 'retreat');
          }
        }
      }
      this._lastPhraseIndex = phraseIndex;
    }

    // --- the structure, on major phrases ---
    this._structTimer += deltaTime;
    if (this._forcedShape) {
      if (this._current !== this._forcedShape) this._activate(this._forcedShape, false);
    } else if (this._structurePhrase >= STRUCTURE_PHRASES || this._structTimer > NO_GRID_STRUCT_S) {
      this._structurePhrase = 0;
      this._structTimer = 0;
      this._activate(this._nextStructure(), false);
    }

    // --- drops ---
    // director.nextEvent carries only {type, score, msUntil} and switches to
    // the following mark the instant one passes, so "msUntil <= 0" never
    // happens. The arrival is latched in this visualizer's own clock instead,
    // exactly as Swarm does it.
    const nextEvent = director && director.nextEvent;
    if (nextEvent && (nextEvent.type === 'drop' || nextEvent.type === 'rise')) {
      const until = nextEvent.msUntil / 1000;
      if (until > 0 && until <= TIGHTEN_BEATS * beatS) {
        this._pendingDropAt = this._time + until;
        this._tightenUntil = this._pendingDropAt;
      }
    }
    if (this._pendingDropAt != null && this._time >= this._pendingDropAt) {
      this._pendingDropAt = null;
      this._explodeUntil = this._time + EXPLODE_BEATS * beatS;
      this._recomposeUntil = this._explodeUntil + RECOMPOSE_BEATS * beatS;
      // The camera is thrown backwards on the drop - and any traversal queued
      // behind it is dropped, because flying into an exploding structure puts
      // the camera outside it before it arrives.
      if (!this._forcedCam) {
        this._queue.length = 0;
        this._startPhase('retreat');
      }
    }
    const exploding = this._time < this._explodeUntil;
    const recomposing = !exploding && this._time < this._recomposeUntil;
    const tightening = !exploding && !recomposing && this._time < this._tightenUntil;

    // --- simulation knobs ---
    let attract = ATTRACT;
    if (tightening) attract *= ATTRACT_TIGHTEN;
    if (recomposing) attract *= 1.5;     // two beats to get back into formation
    if (exploding) attract = 0;
    this._velUniforms.uAttract.value = attract;
    this._velUniforms.uExplode.value = exploding ? EXPLODE_IMPULSE : 0;
    // bands.low -> the instability's amplitude. The structure breathes with the
    // bass, which is the only thing about it that is not still.
    this._velUniforms.uWobbleAmp.value = this._stable
      ? 0 : WOBBLE_AMP * lerp(WOBBLE_LO, WOBBLE_HI, this._lowSmooth);
    this._velUniforms.uMaxSpeed.value = exploding ? MAX_SPEED * 2.2 : MAX_SPEED;

    // --- the camera ---
    // bands.mid -> speed. Applied to the ORBIT RATE only: the four phases are
    // beat-locked by contract (bpm calls all of them), so speeding the clock
    // itself up would decouple the traversal from the music, which is the one
    // thing it must not do. The orbit is the continuous, free part of the
    // trajectory and is where a speed change actually reads.
    const dBeat = deltaTime / beatS;
    if (this._phase === 'orbit') {
      this._theta += (dBeat / ORBIT_BEATS) * Math.PI * 2
        * lerp(CAM_SPEED_LO, CAM_SPEED_HI, this._midSmooth);
    }
    const morphing = this._time < this._morphUntil;
    this._radiusScaleTarget = morphing ? (MORPH_RADIUS / ORBIT_RADIUS)
      : (exploding || recomposing ? 1.30 : 1.0);
    this._placeCamera(dBeat, deltaTime);

    // --- render uniforms ---
    // THE SIZE CEILING opens over the APPROACH, not over the traversal. Eased
    // toward its target only once the traversal had started, it was still at 24
    // when the traversal ended four beats later and the ratio never got past
    // 6.9 - measured, on all six structures. Ramped over the approach it is
    // already wide open at the moment the camera crosses the surface, which is
    // where the near points actually are. The ramp ends where the traversal's
    // constant begins, so nothing jumps.
    let wantMax = POINT_SIZE_MAX;
    if (this._phase === 'through') wantMax = THROUGH_SIZE_MAX;
    else if (this._phase === 'approach') {
      wantMax = lerp(POINT_SIZE_MAX, THROUGH_SIZE_MAX, smoother(this._phaseU));
    }
    // Fast to open (it is tracking a ramp that is already smooth), gentle to
    // close, so the points deflate after the traversal instead of snapping.
    this._sizeMax += (wantMax - this._sizeMax)
      * Math.min(1, deltaTime * (wantMax > this._sizeMax ? 9.0 : 2.2));
    this._pointUniforms.uSizeMax.value = this._sizeMax;
    // bands.high -> individual twinkle, per machine and not as a global pulse.
    this._pointUniforms.uSparkle.value = this._highSmooth * 0.50;
    this._pointUniforms.uWaveGain.value = 0.55 + 0.45 * this._lowSmooth;
    // Inside the structure the near points cover a great deal of the frame, so
    // the whole thing is eased down a little or the traversal blows out.
    const wantIntensity = this._baseIntensity
      * (this._structIntensity != null ? this._structIntensity : 1)
      * (this._phase === 'through' ? 0.72 : 1.0);
    const iu = this._pointUniforms.uIntensity;
    iu.value += (wantIntensity - iu.value) * Math.min(1, deltaTime * 2.2);
  }

  dispose() {
    this._disposed = true;
    this._scene.remove(this._points);
    this._scene.remove(this._stars);
    this._scene.remove(this._bg);
    this._points.geometry.dispose();
    this._pointsMat.dispose();
    this._stars.geometry.dispose();
    this._starMat.dispose();
    this._bg.geometry.dispose();
    this._bgMat.dispose();
    this._simQuad.geometry.dispose();
    this._velMaterial.dispose();
    this._posMaterial.dispose();
    this._pos.dispose();
    this._vel.dispose();
    this._targetTex.dispose();
    this._attrTex.dispose();
    this._zoneTex.dispose();
  }
}

const _origin = new THREE.Vector3(0, 0, 0);
